import { ArrowLeft, Activity, Moon, Wifi } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import welcomeBg from "../../assets/homebg.png";
import BottomNavBar from "../../components/BottomNavBar";

const ImproveAccuracyPage = () => {
  const navigate = useNavigate();

  const steps = [
    {
      icon: Activity,
      iconColor: "text-green-500",
      iconBg: "bg-green-100 dark:bg-green-900/20",
      title: "Connect Fitbit",
      badge: "Done",
      badgeBg: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
      description:
        "Link your Fitbit device to get real-time HRV, heart rate, and sleep data.",
      accuracy: "+20% accuracy",
    },
    {
      icon: Moon,
      iconColor: "text-orange-500",
      iconBg: "bg-orange-100 dark:bg-orange-900/20",
      title: "Wear During Sleep",
      badge: "Partial",
      badgeBg: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
      description:
        "Wearing your tracker overnight enables breathing rate, SpO2, and sleep staging data.",
      accuracy: "+15% accuracy",
    },
    {
      icon: Activity,
      iconColor: "text-gray-500",
      iconBg: "bg-gray-100 dark:bg-gray-800",
      title: "Log Activities",
      badge: "Needed",
      badgeBg: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400",
      description:
        "Recording workouts and active minutes helps calibrate your recovery baseline.",
      accuracy: "+10% accuracy",
    },
    {
      icon: Wifi,
      iconColor: "text-gray-500",
      iconBg: "bg-gray-100 dark:bg-gray-800",
      title: "Sync Regularly",
      badge: "Needed",
      badgeBg: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400",
      description:
        "Sync at least once daily for the most up-to-date stress calculations.",
      accuracy: "+5% accuracy",
    },
  ];

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-20"
        style={{ height: "calc(100vh - 0px)" }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-xl bg-card/80 hover:bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold text-foreground">
            Improve Accuracy
          </h1>
        </div>

        {/* Current Confidence Card */}
        <Card className="rounded-xl bg-white/60 p-4 shadow-sm dark:bg-gray-800/60">
          <div className="mb-3">
            <div className="mb-1 text-sm font-medium text-muted-foreground">
              Current Confidence
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold text-foreground">70%</span>
              <span className="text-sm text-muted-foreground">modeled</span>
            </div>
          </div>
          <div className="mb-2 h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              className="h-full rounded-full bg-gradient-to-r from-blue-500 to-green-500"
              style={{ width: "70%" }}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Complete the steps below to reach 95%+ confidence
          </p>
        </Card>

        {/* Steps to Improve */}
        <div className="space-y-3">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Steps to Improve
          </h2>

          {steps.map((step, index) => (
            <Card
              key={index}
              className="rounded-xl bg-white/60 p-4 shadow-sm dark:bg-gray-800/60"
            >
              <div className="flex gap-3">
                <div
                  className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${step.iconBg}`}
                >
                  <step.icon className={`h-5 w-5 ${step.iconColor}`} />
                </div>
                <div className="flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <h3 className="text-base font-bold text-foreground">
                      {step.title}
                    </h3>
                    <span
                      className={`rounded-md px-2 py-0.5 text-[10px] font-semibold ${step.badgeBg}`}
                    >
                      {step.badge}
                    </span>
                  </div>
                  <p className="mb-2 text-sm text-gray-600 dark:text-gray-400">
                    {step.description}
                  </p>
                  <span className="inline-block rounded-md bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                    {step.accuracy}
                  </span>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* All Steps Complete Preview */}
        <Card className="rounded-xl bg-gradient-to-br from-blue-50/90 to-blue-100/50 p-4 shadow-sm dark:from-blue-950/20 dark:to-blue-900/10">
          <div className="mb-2 text-sm font-medium text-muted-foreground">
            With All Steps Complete
          </div>
          <div className="mb-2 flex items-baseline gap-2">
            <span className="text-4xl font-bold text-blue-600 dark:text-blue-400">
              95%+
            </span>
            <span className="text-sm text-blue-600 dark:text-blue-400">
              confidence
            </span>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Full signal coverage enables precise, real-time stress index
            calculations with minimal modeling assumptions.
          </p>
        </Card>
      </div>

      {/* Bottom Navigation Bar */}
      <BottomNavBar activeIndex={3} />
    </div>
  );
};

export default ImproveAccuracyPage;
